import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { mediaUrl } from '@/hooks/useMediaUrl';
import type { NoteWithAuthor } from '@shared/types';

type View = 'list' | 'detail' | 'editor';

export function NotesPage() {
  const { id: routeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const [view, setView] = useState<View>('list');
  const [notes, setNotes] = useState<NoteWithAuthor[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedNote, setSelectedNote] = useState<NoteWithAuthor | null>(null);
  const [loadingNote, setLoadingNote] = useState(false);

  // Editor state
  const [editingNote, setEditingNote] = useState<NoteWithAuthor | null>(null);
  const [editorTitle, setEditorTitle] = useState('');
  const [editorContent, setEditorContent] = useState('');
  const [editorVisibility, setEditorVisibility] = useState<'public' | 'friends'>('public');
  const [saving, setSaving] = useState(false);

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const data = await api.get<NoteWithAuthor[]>('/notes');
      setNotes(data);
    } catch {
      // ignore
    }
    setLoading(false);
  };

  const fetchNote = async (noteId: string) => {
    setLoadingNote(true);
    try {
      const data = await api.get<NoteWithAuthor>(`/notes/${noteId}`);
      setSelectedNote(data);
      setView('detail');
    } catch {
      // ignore
    }
    setLoadingNote(false);
  };

  useEffect(() => {
    fetchNotes();
  }, []);

  // Handle route param for direct note linking
  useEffect(() => {
    if (routeId) {
      fetchNote(routeId);
    } else {
      setView('list');
      setSelectedNote(null);
    }
  }, [routeId]);

  const openNote = (note: NoteWithAuthor) => {
    setSelectedNote(note);
    setView('detail');
    navigate(`/notes/${note.id}`, { replace: true });
  };

  const goBack = () => {
    setView('list');
    setSelectedNote(null);
    navigate('/notes', { replace: true });
  };

  const openEditor = (note?: NoteWithAuthor) => {
    if (note) {
      setEditingNote(note);
      setEditorTitle(note.title);
      setEditorContent(note.content);
      setEditorVisibility(note.visibility);
    } else {
      setEditingNote(null);
      setEditorTitle('');
      setEditorContent('');
      setEditorVisibility('public');
    }
    setView('editor');
  };

  const handleSave = async () => {
    if (!editorTitle.trim() || !editorContent.trim()) return;
    setSaving(true);
    try {
      if (editingNote) {
        await api.put(`/notes/${editingNote.id}`, {
          title: editorTitle.trim(),
          content: editorContent.trim(),
          visibility: editorVisibility,
        });
      } else {
        await api.post('/notes', {
          title: editorTitle.trim(),
          content: editorContent.trim(),
          visibility: editorVisibility,
        });
      }
      await fetchNotes();
      setView('list');
      navigate('/notes', { replace: true });
    } catch {
      // ignore
    }
    setSaving(false);
  };

  const timeAgo = (dateStr: string) => {
    const now = new Date();
    const then = new Date(dateStr + 'Z');
    const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return then.toLocaleDateString();
  };

  // ===== LIST VIEW =====
  if (view === 'list') {
    return (
      <div className="max-w-3xl mx-auto px-3 py-4">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-lg font-bold text-sand-800">&#128221; Notes</h1>
          <button onClick={() => openEditor()} className="btn-primary text-xs py-1.5 px-4">
            Write a Note
          </button>
        </div>

        {loading ? (
          <p className="text-center text-sand-500 text-xs py-8">Loading notes...</p>
        ) : notes.length === 0 ? (
          <div className="text-center py-8 text-sand-500">
            <div className="text-3xl mb-2">&#128221;</div>
            <p className="text-sm">No notes yet. Write the first one!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {notes.map((note) => (
              <div key={note.id} className="card hover:shadow-md transition-shadow">
                <div className="p-4">
                  <button onClick={() => openNote(note)} className="text-left w-full">
                    <h2 className="text-sm font-bold text-sand-800 hover:text-vibe-600 transition-colors mb-1">
                      {note.title}
                    </h2>
                  </button>
                  <p className="text-xs text-sand-600 mb-2 line-clamp-3">
                    {note.content.slice(0, 200)}{note.content.length > 200 ? '...' : ''}
                  </p>
                  <div className="flex items-center gap-2 text-[10px] text-sand-500">
                    <Link to={`/profile/${note.author_username}`} className="flex items-center gap-1 hover:text-sand-700">
                      <img src={mediaUrl(note.author_avatar_key)} alt="" className="w-4 h-4 rounded object-cover" />
                      <span className="font-bold">{note.author_display_name}</span>
                    </Link>
                    <span>&middot;</span>
                    <span>{timeAgo(note.created_at)}</span>
                    {note.like_count > 0 && (
                      <>
                        <span>&middot;</span>
                        <span>{note.like_count} like{note.like_count !== 1 ? 's' : ''}</span>
                      </>
                    )}
                    {note.comment_count > 0 && (
                      <>
                        <span>&middot;</span>
                        <span>{note.comment_count} comment{note.comment_count !== 1 ? 's' : ''}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ===== DETAIL VIEW =====
  if (view === 'detail' && (selectedNote || loadingNote)) {
    if (loadingNote) {
      return (
        <div className="max-w-3xl mx-auto px-3 py-8 text-center text-sand-500">
          <p className="text-sm">Loading note...</p>
        </div>
      );
    }
    if (!selectedNote) {
      return (
        <div className="max-w-3xl mx-auto px-3 py-8 text-center text-sand-500">
          <p className="text-sm">Note not found</p>
        </div>
      );
    }
    const isAuthor = user?.id === selectedNote.author_id;
    return (
      <div className="max-w-3xl mx-auto px-3 py-4">
        <div className="mb-4">
          <button onClick={goBack} className="text-xs text-vibe-500 hover:text-vibe-600 font-bold">
            &larr; Back to Notes
          </button>
        </div>
        <div className="card">
          <div className="p-6">
            <h1 className="text-xl font-bold text-sand-800 mb-3">{selectedNote.title}</h1>
            <div className="flex items-center gap-2 mb-4 pb-4 border-b border-sand-200">
              <Link to={`/profile/${selectedNote.author_username}`}>
                <img src={mediaUrl(selectedNote.author_avatar_key)} alt="" className="w-8 h-8 rounded object-cover" />
              </Link>
              <div>
                <Link to={`/profile/${selectedNote.author_username}`} className="link font-bold text-xs">
                  {selectedNote.author_display_name}
                </Link>
                <div className="text-[10px] text-sand-500">{timeAgo(selectedNote.created_at)}</div>
              </div>
              {isAuthor && (
                <button onClick={() => openEditor(selectedNote)} className="ml-auto text-xs text-vibe-500 hover:text-vibe-600 font-bold">
                  Edit
                </button>
              )}
            </div>
            <div className="text-sm text-sand-800 leading-relaxed whitespace-pre-wrap">
              {selectedNote.content}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== EDITOR VIEW =====
  return (
    <div className="max-w-3xl mx-auto px-3 py-4">
      <div className="mb-4">
        <button onClick={goBack} className="text-xs text-vibe-500 hover:text-vibe-600 font-bold">
          &larr; Back to Notes
        </button>
      </div>
      <div className="card">
        <div className="card-header">
          {editingNote ? 'Edit Note' : 'Write a New Note'}
        </div>
        <div className="p-4 space-y-3">
          <input
            value={editorTitle}
            onChange={(e) => setEditorTitle(e.target.value)}
            placeholder="Note title"
            className="input w-full text-sm font-bold"
          />
          <textarea
            value={editorContent}
            onChange={(e) => setEditorContent(e.target.value)}
            placeholder="Write your note..."
            className="input w-full text-sm resize-none"
            rows={15}
          />
          <div className="flex items-center gap-3">
            <label className="text-[10px] font-bold text-sand-500 uppercase">Visibility:</label>
            <select
              value={editorVisibility}
              onChange={(e) => setEditorVisibility(e.target.value as 'public' | 'friends')}
              className="input text-xs"
            >
              <option value="public">Public</option>
              <option value="friends">Friends Only</option>
            </select>
          </div>
          <div className="flex gap-2 pt-2">
            <button
              onClick={handleSave}
              disabled={saving || !editorTitle.trim() || !editorContent.trim()}
              className="btn-primary text-xs py-1.5 px-4 disabled:opacity-40"
            >
              {saving ? 'Saving...' : editingNote ? 'Update Note' : 'Publish Note'}
            </button>
            <button onClick={goBack} className="btn-secondary text-xs py-1.5 px-4">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
